import React, { useState } from 'react';
import { Search, Sparkles, ChevronDown, ChevronUp, CheckCircle, AlertCircle, XCircle, TrendingUp, Send, Users, Building } from 'lucide-react';
// Mock data per gli immobili
const mockProperties = [{
  id: 1,
  address: 'Via della Spiga 8, Milano',
  zone: 'Quadrilatero',
  price: 650000,
  size: 90,
  type: 'Boutique',
  floor: 'Piano Terra'
}, {
  id: 2,
  address: 'Via Montenapoleone 12, Milano',
  zone: 'Centro',
  price: 850000,
  size: 120,
  type: 'Retail',
  floor: 'Piano Terra'
}, {
  id: 3,
  address: 'Corso Venezia 45, Milano',
  zone: 'Centro',
  price: 1200000,
  size: 200,
  type: 'Showroom',
  floor: 'Piano Terra + Mezzanino'
}];
// Mock data per le aziende matchate
const mockMatchedCompanies = [{
  id: 1,
  company: 'Jewelry Brand',
  logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xop1kjUZWavx3D9YfBpk26/eugenio.png',
  requirement: 'Boutique esclusiva 80-100m² in Via Montenapoleone o Via della Spiga. Budget fino a €900.000. Richiesto piano terra con possibilità di personalizzazione.',
  matchScore: 95,
  satisfied: ['Posizione in Via della Spiga come richiesto', 'Superficie 90m² perfettamente nel range', 'Piano terra come richiesto', 'Prezzo €650.000 ben sotto il budget massimo'],
  acceptable: ['Tipologia Boutique compatibile con richiesta', 'Zona Quadrilatero ideale per brand jewelry'],
  notSatisfied: []
}, {
  id: 2,
  company: 'Luxury Fashion Brand',
  logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xop1kjUZWavx3D9YfBpk26/eugenio.png',
  requirement: 'Cerchiamo uno spazio retail di circa 150m² in zona Quadrilatero della Moda, preferibilmente su strada ad alto traffico. Budget fino a €1.000.000.',
  matchScore: 82,
  satisfied: ['Zona Quadrilatero come richiesto', 'Via della Spiga ad altissimo traffico', 'Piano terra con vetrine', 'Prezzo sotto budget'],
  acceptable: ['Superficie 90m² inferiore ai 150m² richiesti ma comunque utilizzabile'],
  notSatisfied: ['Metratura significativamente inferiore al target di 150m²']
}, {
  id: 3,
  company: 'Premium Accessories',
  logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xop1kjUZWavx3D9YfBpk26/eugenio.png',
  requirement: 'Boutique 70-100m² in zona di prestigio, budget €600.000-€800.000. Preferenza per Via della Spiga o vie limitrofe.',
  matchScore: 88,
  satisfied: ['Posizione perfetta in Via della Spiga', 'Superficie 90m² nel range richiesto', 'Prezzo €650.000 nel budget', 'Piano terra ideale'],
  acceptable: ['Tipologia Boutique perfettamente allineata'],
  notSatisfied: []
}];
type ViewState = 'initial' | 'loading' | 'results';
export function PropertyMatching() {
  const [viewState, setViewState] = useState<ViewState>('initial');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [expandedCompany, setExpandedCompany] = useState<number | null>(null);
  const [analyzedProperty, setAnalyzedProperty] = useState<any>(null);
  const handleSearch = () => {
    const property = mockProperties.find(p => p.id.toString() === selectedProperty);
    if (!property) return;
    setAnalyzedProperty(property);
    setViewState('loading');
    setTimeout(() => {
      setViewState('results');
    }, 3000);
  };
  const handleReset = () => {
    setViewState('initial');
    setSelectedProperty('');
    setExpandedCompany(null);
    setAnalyzedProperty(null);
  };
  const toggleCompany = (id: number) => {
    setExpandedCompany(expandedCompany === id ? null : id);
  };
  if (viewState === 'initial') {
    return <div className="space-y-6" data-id="element-3389">
        <div className="flex items-center justify-between" data-id="element-3390">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3391">
            Property Matching
          </h1>
        </div>
        <div className="bg-white p-6 border border-gray-200 rounded-lg space-y-6" data-id="element-3392">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3393">
            <Sparkles className="w-5 h-5 text-[#1388ad] flex-shrink-0 mt-0.5" data-id="element-3394" />
            <div data-id="element-3395">
              <h3 className="text-sm font-normal text-gray-900 mb-1" data-id="element-3396">
                Matching Intelligente con AI
              </h3>
              <p className="text-xs text-gray-600" data-id="element-3397">
                L'intelligenza artificiale analizzerà l'immobile e troverà le
                aziende ed esigenze più compatibili, fornendo una spiegazione
                dettagliata per ogni match.
              </p>
            </div>
          </div>
          <div className="space-y-4" data-id="element-3398">
            <div data-id="element-3399">
              <label className="block text-sm font-normal text-gray-700 mb-2" data-id="element-3400">
                Seleziona un immobile
              </label>
              <select value={selectedProperty} onChange={e => setSelectedProperty(e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-3401">
                <option value="" data-id="element-3402">-- Seleziona un immobile --</option>
                {mockProperties.map(prop => <option key={prop.id} value={prop.id} data-id="element-3403">
                    {prop.address} - {prop.size}m² - €
                    {prop.price.toLocaleString()}
                  </option>)}
              </select>
              {selectedProperty && <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2" data-id="element-3404">
                  <div className="flex items-center gap-3 text-xs text-gray-600" data-id="element-3405">
                    <span className="px-2 py-1 bg-gray-100 rounded-full" data-id="element-3406">
                      {mockProperties.find(p => p.id.toString() === selectedProperty)?.type}
                    </span>
                    <span data-id="element-3407">
                      {mockProperties.find(p => p.id.toString() === selectedProperty)?.size}{' '}
                      m²
                    </span>
                    <span data-id="element-3408">
                      {mockProperties.find(p => p.id.toString() === selectedProperty)?.floor}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3409">
                    {mockProperties.find(p => p.id.toString() === selectedProperty)?.zone}
                  </p>
                </div>}
            </div>
          </div>
          <button onClick={handleSearch} disabled={!selectedProperty} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#1388ad] text-white text-sm font-normal hover:bg-[#0f6b8a] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors rounded-full" data-id="element-3410">
            <Search className="w-4 h-4" data-id="element-3411" />
            Trova Aziende Interessate
          </button>
        </div>
      </div>;
  }
  if (viewState === 'loading') {
    return <div className="space-y-6" data-id="element-3412">
        <div className="flex items-center justify-between" data-id="element-3413">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3414">
            Property Matching
          </h1>
        </div>
        <div className="bg-white p-8 border border-gray-200 rounded-lg" data-id="element-3415">
          <div className="flex flex-col items-center justify-center py-12" data-id="element-3416">
            <div className="relative w-16 h-16 mb-6" data-id="element-3417">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full" data-id="element-3418" />
              <div className="absolute inset-0 border-4 border-[#1388ad] rounded-full border-t-transparent animate-spin" data-id="element-3419" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-[#1388ad]" data-id="element-3420" />
            </div>
            <h3 className="text-lg font-normal text-gray-900 mb-2" data-id="element-3421">
              Analisi in corso...
            </h3>
            <p className="text-sm text-gray-600 text-center max-w-md" data-id="element-3422">
              L'AI sta analizzando l'immobile e cercando le aziende più
              interessate nel database
            </p>
          </div>
          <div className="space-y-4 mt-8" data-id="element-3423">
            {[1, 2, 3].map(i => <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse" data-id="element-3424">
                <div className="flex gap-4" data-id="element-3425">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" data-id="element-3426" />
                  <div className="flex-1 space-y-3" data-id="element-3427">
                    <div className="h-4 bg-gray-200 rounded w-3/4" data-id="element-3428" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" data-id="element-3429" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" data-id="element-3430" />
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3431">
      <div className="flex items-center justify-between" data-id="element-3432">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3433">
          Property Matching
        </h1>
        <div className="flex items-center gap-3" data-id="element-3434">
          <button onClick={handleReset} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-full border border-gray-300" data-id="element-3435">
            Nuova Ricerca
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-full hover:bg-[#0f6b8a] transition-colors" data-id="element-3436">
            <Send className="w-4 h-4" data-id="element-3437" />
            Invia Campagna
          </button>
        </div>
      </div>
      <div className="bg-gradient-to-r from-[#1388ad] to-[#0f6b8a] p-6 border border-gray-200 rounded-lg text-white" data-id="element-3438">
        <div className="flex items-start gap-3 mb-3" data-id="element-3439">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-3440" />
          <h3 className="text-sm font-normal" data-id="element-3441">Immobile Analizzato</h3>
        </div>
        <div className="space-y-2" data-id="element-3442">
          <p className="text-sm opacity-90 font-normal" data-id="element-3443">
            {analyzedProperty?.address}
          </p>
          <div className="flex items-center gap-3 text-xs opacity-80" data-id="element-3444">
            <span data-id="element-3445">{analyzedProperty?.type}</span>
            <span data-id="element-3446">•</span>
            <span data-id="element-3447">{analyzedProperty?.size} m²</span>
            <span data-id="element-3448">•</span>
            <span data-id="element-3449">{analyzedProperty?.floor}</span>
            <span data-id="element-3450">•</span>
            <span data-id="element-3451">€{analyzedProperty?.price.toLocaleString()}</span>
          </div>
        </div>
      </div>
      <div className="bg-white p-4 border border-gray-200 rounded-lg" data-id="element-3452">
        <div className="flex items-center justify-between" data-id="element-3453">
          <div className="flex items-center gap-2" data-id="element-3454">
            <Users className="w-5 h-5 text-[#1388ad]" data-id="element-3455" />
            <h2 className="text-lg font-normal text-gray-900" data-id="element-3456">
              {mockMatchedCompanies.length} Aziende Interessate
            </h2>
          </div>
          <span className="text-xs text-gray-500" data-id="element-3457">
            Ordinate per compatibilità
          </span>
        </div>
      </div>
      <div className="space-y-4" data-id="element-3458">
        {mockMatchedCompanies.map(company => <div key={company.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-3459">
            <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-3460">
              <div className="flex items-center justify-between gap-3" data-id="element-3461">
                <div className="flex-1 min-w-0" data-id="element-3462">
                  <h3 className="text-base font-semibold text-gray-900" data-id="element-3463">
                    {company.company}
                  </h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3464">
                  <div className="text-right" data-id="element-3465">
                    <div className="text-2xl font-semibold text-[#1388ad]" data-id="element-3466">
                      {company.matchScore}%
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3467">compatibilità</div>
                  </div>
                  <TrendingUp className="w-5 h-5 text-[#1388ad]" data-id="element-3468" />
                </div>
              </div>
            </div>
            <div className="p-4" data-id="element-3469">
              <p className="text-xs text-gray-600" data-id="element-3470">{company.requirement}</p>
            </div>
            <div className="border-t border-gray-200" data-id="element-3471">
              <button onClick={() => toggleCompany(company.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-3472">
                <span className="text-sm text-gray-700" data-id="element-3473">Dettagli Matching</span>
                {expandedCompany === company.id ? <ChevronUp className="w-4 h-4 text-gray-500" data-id="element-3474" /> : <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-3475" />}
              </button>
              {expandedCompany === company.id && <div className="px-4 pb-4 space-y-4" data-id="element-3476">
                  {company.satisfied.length > 0 && <div data-id="element-3477">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3478">
                        <CheckCircle className="w-4 h-4 text-green-600" data-id="element-3479" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3480">
                          Requisiti Soddisfatti
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3481">
                        {company.satisfied.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3482">
                            <span className="text-green-600 mt-0.5" data-id="element-3483">•</span>
                            <span data-id="element-3484">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {company.acceptable.length > 0 && <div data-id="element-3485">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3486">
                        <AlertCircle className="w-4 h-4 text-yellow-600" data-id="element-3487" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3488">
                          Compromessi Accettabili
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3489">
                        {company.acceptable.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3490">
                            <span className="text-yellow-600 mt-0.5" data-id="element-3491">•</span>
                            <span data-id="element-3492">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {company.notSatisfied.length > 0 && <div data-id="element-3493">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3494">
                        <XCircle className="w-4 h-4 text-red-600" data-id="element-3495" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3496">
                          Requisiti Non Soddisfatti
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3497">
                        {company.notSatisfied.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3498">
                            <span className="text-red-600 mt-0.5" data-id="element-3499">•</span>
                            <span data-id="element-3500">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                </div>}
            </div>
          </div>)}
      </div>
    </div>;
}